import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  CustomInput,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import Swal from "sweetalert2";
import { Country, State, City } from "country-state-city";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  getDestinations,
  getDestinationByUuid,
  addDestination,
  updateDestination,
  toggleDestinationStatus,
  toggleDestinationPopular,
  deleteDestination,
} from "../../api/ApiServices";
import {
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaStar,
  FaRegStar,
  FaEdit,
  FaTrash,
  FaEye,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaImage,
  FaSortNumericDown,
  FaCompass,
  FaRoute,
  FaUserTie,
  FaFilter,
} from "react-icons/fa";

const ITEMS_PER_PAGE = 10;
const allCountries = Country.getAllCountries().map((c) => ({
  label: c.name,
  value: c.name,
  isoCode: c.isoCode,
}));

const modalSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: "0.375rem",
    minHeight: "44px",
    borderColor: state.isFocused ? "#5e72e4" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(94, 114, 228, 0.25)" : "none",
    "&:hover": { borderColor: "#5e72e4" },
    fontSize: "14px",
    backgroundColor: "#fff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f6f9fc" : "#fff",
    color: state.isSelected ? "#fff" : "#32325d",
    fontSize: "14px",
    cursor: "pointer",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: "0.375rem",
    boxShadow: "0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07)",
  }),
};

const PopularDestinations = () => {
  // State
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalDestinations: 0,
    totalActive: 0,
    totalInactive: 0,
    totalPopular: 0,
  });

  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [popularFilter, setPopularFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("displayOrder");
  const [sortOrder, setSortOrder] = useState("asc");

  // Filter Bar State Options (based on countryFilter)
  const filterStateOptions = React.useMemo(() => {
    if (!countryFilter || countryFilter === "all") return [];
    const cObj = Country.getAllCountries().find(
      (c) => c.name.toLowerCase() === countryFilter.toLowerCase()
    );
    if (!cObj) return [];
    return State.getStatesOfCountry(cObj.isoCode).map((s) => ({
      label: s.name,
      value: s.name,
    }));
  }, [countryFilter]);

  // Modals
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [imagePreviewModalOpen, setImagePreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [previewImageTitle, setPreviewImageTitle] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    country: "",
    state: "",
    city: "",
    description: "",
    latitude: "",
    longitude: "",
    displayOrder: 0,
    isPopular: true,
    status: "active",
    image: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Form State Options (dependent on formData.country)
  const formStateOptions = React.useMemo(() => {
    if (!formData.country) return [];
    const cObj = Country.getAllCountries().find(
      (c) => c.name.toLowerCase() === formData.country.toLowerCase()
    );
    if (!cObj) return [];
    const states = State.getStatesOfCountry(cObj.isoCode) || [];
    return states.map((s) => ({
      label: s.name,
      value: s.name,
      isoCode: s.isoCode,
    }));
  }, [formData.country]);

  // Form City Options (dependent on formData.country and formData.state)
  const formCityOptions = React.useMemo(() => {
    if (!formData.country || !formData.state) return [];
    const cObj = Country.getAllCountries().find(
      (c) => c.name.toLowerCase() === formData.country.toLowerCase()
    );
    if (!cObj) return [];
    const states = State.getStatesOfCountry(cObj.isoCode) || [];
    const sObj = states.find(
      (s) => s.name.toLowerCase() === formData.state.toLowerCase()
    );
    if (!sObj) return [];
    const cities = City.getCitiesOfState(cObj.isoCode, sObj.isoCode) || [];
    return cities.map((city) => ({
      label: city.name,
      value: city.name,
    }));
  }, [formData.country, formData.state]);

  /* =====================================================
     FETCH DESTINATIONS
  ===================================================== */
  const fetchDestinationsList = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await getDestinations({
          search: searchTerm,
          country: countryFilter !== "all" ? countryFilter : "",
          state: stateFilter !== "all" ? stateFilter : "",
          status: statusFilter,
          isPopular: popularFilter,
          page,
          limit: ITEMS_PER_PAGE,
          sortBy,
          sortOrder,
        });

        if (res?.success) {
          setDestinations(res.data || []);
          setTotalItems(res.pagination?.totalItems || 0);
          setTotalPages(res.pagination?.totalPages || 1);
          setCurrentPage(res.pagination?.currentPage || 1);
          if (res.stats) {
            setStats(res.stats);
          }
        } else {
          setDestinations([]);
        }
      } catch (err) {
        console.error("Error loading destinations:", err);
        Swal.fire({
          title: "Error",
          text: err.message || "Failed to load destinations",
          icon: "error",
          confirmButtonColor: "#5e72e4",
        });
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, countryFilter, stateFilter, statusFilter, popularFilter, sortBy, sortOrder]
  );

  // Debounced search / filter trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDestinationsList(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchDestinationsList]);

  /* =====================================================
     FORM VALIDATION
  ===================================================== */
  const validateField = (field, value) => {
    let error = "";
    if (field === "name") {
      if (!value || !value.trim()) {
        error = "Destination name is required";
      } else if (value.trim().length < 2) {
        error = "Name must be at least 2 characters";
      }
    }
    if (field === "country") {
      if (!value || !value.trim()) {
        error = "Country is required";
      }
    }
    if (field === "displayOrder") {
      if (isNaN(parseInt(value, 10)) || parseInt(value, 10) < 0) {
        error = "Display order must be a positive number";
      }
    }
    if (field === "latitude" && value !== "" && value !== null && value !== undefined) {
      const lat = parseFloat(value);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        error = "Latitude must be between -90 and 90";
      }
    }
    if (field === "longitude" && value !== "" && value !== null && value !== undefined) {
      const lng = parseFloat(value);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        error = "Longitude must be between -180 and 180";
      }
    }
    return error;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    const error = validateField(field, value);
    setValidationErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleInputBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setValidationErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateAllFields = () => {
    const errors = {
      name: validateField("name", formData.name),
      country: validateField("country", formData.country),
      displayOrder: validateField("displayOrder", formData.displayOrder),
    };
    setValidationErrors(errors);
    setTouched({
      name: true,
      country: true,
      displayOrder: true,
    });
    return !Object.values(errors).some((e) => e);
  };

  /* =====================================================
     IMAGE HANDLING
  ===================================================== */
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        Swal.fire("Invalid File", "Please select an image file (PNG, JPG, WebP)", "warning");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSelectedImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  /* =====================================================
     MODAL CONTROLS
  ===================================================== */
  const openCreateModal = () => {
    setEditMode(false);
    setEditingUuid(null);
    setFormData({
      name: "",
      country: "",
      state: "",
      city: "",
      description: "",
      latitude: "",
      longitude: "",
      displayOrder: (totalItems || 0) + 1,
      isPopular: true,
      status: "active",
      image: "",
    });
    setImageFile(null);
    setImagePreview("");
    setValidationErrors({});
    setTouched({});
    setFormModalOpen(true);
  };

  const openEditModal = (item) => {
    if (!item) return;
    setEditMode(true);
    setEditingUuid(item.uuid || item._id);
    setFormData({
      name: item.name || "",
      country: item.country || "",
      state: item.state || "",
      city: item.city || "",
      description: item.description || "",
      latitude: item.latitude !== undefined && item.latitude !== null ? item.latitude : "",
      longitude: item.longitude !== undefined && item.longitude !== null ? item.longitude : "",
      displayOrder: item.displayOrder !== undefined ? item.displayOrder : 0,
      isPopular: item.isPopular !== undefined ? item.isPopular : true,
      status: item.status || "active",
      image: item.image || "",
    });
    setImageFile(null);
    setImagePreview(item.imageUrl || item.image || "");
    setValidationErrors({});
    setTouched({});
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setSubmitting(false);
  };

  const openDetailModal = async (item) => {
    if (!item) return;
    setSelectedDestination(item);
    setDetailModalOpen(true);
    setDetailLoading(true);
    const targetId = item.uuid || item._id;
    if (targetId) {
      try {
        const res = await getDestinationByUuid(targetId);
        if (res?.success && res.data) {
          setSelectedDestination(res.data);
        }
      } catch (err) {
        console.error("Error getting destination details:", err);
      } finally {
        setDetailLoading(false);
      }
    } else {
      setDetailLoading(false);
    }
  };

  const openImagePreview = (url, title) => {
    if (!url) return;
    setPreviewImageUrl(url);
    setPreviewImageTitle(title || "Destination Image");
    setImagePreviewModalOpen(true);
  };

  /* =====================================================
     FORM SUBMIT
  ===================================================== */
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateAllFields()) {
      return;
    }

    setSubmitting(true);
    try {
      let payload;
      if (imageFile) {
        payload = new FormData();
        payload.append("name", formData.name.trim());
        payload.append("country", formData.country.trim());
        payload.append("state", formData.state.trim());
        payload.append("city", formData.city.trim());
        payload.append("description", formData.description.trim());
        payload.append("latitude", formData.latitude !== "" ? formData.latitude : "");
        payload.append("longitude", formData.longitude !== "" ? formData.longitude : "");
        payload.append("displayOrder", formData.displayOrder);
        payload.append("isPopular", formData.isPopular);
        payload.append("status", formData.status);
        payload.append("image", imageFile);
      } else {
        payload = {
          name: formData.name.trim(),
          country: formData.country.trim(),
          state: formData.state.trim(),
          city: formData.city.trim(),
          description: formData.description.trim(),
          latitude: formData.latitude !== "" && !isNaN(parseFloat(formData.latitude)) ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude !== "" && !isNaN(parseFloat(formData.longitude)) ? parseFloat(formData.longitude) : null,
          displayOrder: parseInt(formData.displayOrder, 10) || 0,
          isPopular: formData.isPopular,
          status: formData.status,
          image: formData.image,
        };
      }

      if (editMode && editingUuid) {
        const res = await updateDestination(editingUuid, payload);
        if (res?.success) {
          Swal.fire({
            title: "Updated!",
            text: "Destination updated successfully",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          closeFormModal();
          fetchDestinationsList(currentPage);
        }
      } else {
        const res = await addDestination(payload);
        if (res?.success) {
          Swal.fire({
            title: "Created!",
            text: "Destination added successfully",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          closeFormModal();
          fetchDestinationsList(1);
        }
      }
    } catch (err) {
      console.error("Submit destination error:", err);
      Swal.fire({
        title: "Submission Failed",
        text: err.message || "Failed to save destination. Please check the inputs.",
        icon: "error",
        confirmButtonColor: "#5e72e4",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* =====================================================
     TOGGLE STATUS & POPULAR
  ===================================================== */
  const handleToggleStatus = async (item) => {
    if (!item) return;
    const targetId = item.uuid || item._id;
    if (!targetId) return;
    const newStatus = item.status === "active" ? "inactive" : "active";
    try {
      const res = await toggleDestinationStatus(targetId, { status: newStatus });
      if (res?.success) {
        setDestinations((prev) =>
          prev.map((d) => ((d.uuid === targetId || d._id === targetId) ? { ...d, status: newStatus } : d))
        );
        setStats((prev) => ({
          ...prev,
          totalActive: newStatus === "active" ? prev.totalActive + 1 : prev.totalActive - 1,
          totalInactive: newStatus === "inactive" ? prev.totalInactive + 1 : prev.totalInactive - 1,
        }));
        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: "success",
          title: `Destination marked as ${newStatus}`,
        });
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to update status", "error");
    }
  };

  const handleTogglePopular = async (item) => {
    if (!item) return;
    const targetId = item.uuid || item._id;
    if (!targetId) return;
    const newPopular = !item.isPopular;
    try {
      const res = await toggleDestinationPopular(targetId, { isPopular: newPopular });
      if (res?.success) {
        setDestinations((prev) =>
          prev.map((d) => ((d.uuid === targetId || d._id === targetId) ? { ...d, isPopular: newPopular } : d))
        );
        setStats((prev) => ({
          ...prev,
          totalPopular: newPopular ? prev.totalPopular + 1 : prev.totalPopular - 1,
        }));
        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: "success",
          title: newPopular ? "Marked as Popular on App" : "Removed from Popular",
        });
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to toggle popular status", "error");
    }
  };

  /* =====================================================
     DELETE DESTINATION
  ===================================================== */
  const handleDeleteDestination = async (item) => {
    if (!item) return;
    const targetId = item.uuid || item._id;
    if (!targetId) return;
    const confirm = await Swal.fire({
      title: "Delete Destination?",
      html: `Are you sure you want to delete <strong>${item.name}</strong> (${item.country})?<br/><small class="text-muted">It will no longer appear on the Area Trek app.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f5365c",
      cancelButtonColor: "#8898aa",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await deleteDestination(targetId);
      if (res?.success) {
        Swal.fire({
          title: "Deleted!",
          text: "Destination has been removed",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchDestinationsList(currentPage);
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to delete destination", "error");
    }
  };

  const resetAllFilters = () => {
    setSearchTerm("");
    setCountryFilter("all");
    setStateFilter("all");
    setStatusFilter("all");
    setPopularFilter("all");
    setSortBy("displayOrder");
    setSortOrder("asc");
  };

  return (
    <>
      <CommonHeader />

      <Container className="mt--7" fluid>
        {/* ================= STATS CARDS ================= */}
        <Row className="mb-4">
          <Col lg="3" sm="6" className="mb-3 mb-lg-0">
            <Card className="card-stats shadow-sm border-0 rounded-lg">
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="text-uppercase text-muted ls-1 mb-1 font-weight-bold" style={{ fontSize: "11px" }}>
                      Total Destinations
                    </h6>
                    <span className="h2 font-weight-bold mb-0 text-dark">
                      {stats.totalDestinations}
                    </span>
                  </Col>
                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow"
                      style={{
                        background: "linear-gradient(87deg, #5e72e4 0, #825ee4 100%)",
                        width: "48px",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaGlobeAmericas size={20} />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg="3" sm="6" className="mb-3 mb-lg-0">
            <Card className="card-stats shadow-sm border-0 rounded-lg">
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="text-uppercase text-muted ls-1 mb-1 font-weight-bold" style={{ fontSize: "11px" }}>
                      Popular on App
                    </h6>
                    <span className="h2 font-weight-bold mb-0 text-warning">
                      {stats.totalPopular}
                    </span>
                  </Col>
                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow"
                      style={{
                        background: "linear-gradient(87deg, #fb6340 0, #fbb140 100%)",
                        width: "48px",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaStar size={20} />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg="3" sm="6" className="mb-3 mb-lg-0">
            <Card className="card-stats shadow-sm border-0 rounded-lg">
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="text-uppercase text-muted ls-1 mb-1 font-weight-bold" style={{ fontSize: "11px" }}>
                      Active Destinations
                    </h6>
                    <span className="h2 font-weight-bold mb-0 text-success">
                      {stats.totalActive}
                    </span>
                  </Col>
                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow"
                      style={{
                        background: "linear-gradient(87deg, #2dce89 0, #2dcecc 100%)",
                        width: "48px",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaCheckCircle size={20} />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg="3" sm="6">
            <Card className="card-stats shadow-sm border-0 rounded-lg">
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="text-uppercase text-muted ls-1 mb-1 font-weight-bold" style={{ fontSize: "11px" }}>
                      Inactive / Hidden
                    </h6>
                    <span className="h2 font-weight-bold mb-0 text-danger">
                      {stats.totalInactive}
                    </span>
                  </Col>
                  <Col className="col-auto">
                    <div
                      className="icon icon-shape text-white rounded-circle shadow"
                      style={{
                        background: "linear-gradient(87deg, #f5365c 0, #f56036 100%)",
                        width: "48px",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaTimesCircle size={20} />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ================= MAIN CARD ================= */}
        <Row>
          <Col>
            <Card className="shadow border-0 rounded-lg">
              <CardHeader className="bg-white border-0 pt-4 pb-3">
                <Row className="align-items-center">
                  <Col md="6">
                    <div className="d-flex align-items-center">
                      <div
                        className="mr-3 p-2 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center shadow-sm"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <FaCompass size={20} />
                      </div>
                      <div>
                        <h3 className="mb-0 font-weight-bold text-dark">
                          Popular Destination Management
                        </h3>
                        <p className="text-muted mb-0 small">
                          Control travel destinations shown to travelers on Area Trek App
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col md="6" className="text-md-right mt-3 mt-md-0">
                    <Button
                      color="light"
                      size="sm"
                      className="mr-2 border text-muted shadow-sm"
                      onClick={() => fetchDestinationsList(currentPage)}
                      title="Refresh Data"
                    >
                      <FaSyncAlt className={loading ? "fa-spin" : ""} /> Refresh
                    </Button>
                    <Button
                      size="sm"
                      onClick={openCreateModal}
                      className="bg-button-gradient-primary shadow-sm text-white font-weight-bold"
                      style={{
                        padding: "8px 18px",
                        borderRadius: "8px",
                      }}
                    >
                      <FaPlus className="mr-1" /> Add Destination
                    </Button>
                  </Col>
                </Row>

                {/* ================= FILTERS ================= */}
                <Row className="mt-4 pt-3 border-top align-items-center" style={{ gap: "10px 0" }}>
                  <Col lg="4" md="6">
                    <InputGroup className="shadow-none border rounded">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText className="bg-transparent border-0 text-muted">
                          <FaSearch />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        // type="search"
                        placeholder="Search destination, city, country..."
                        className="border-0 shadow-none pl-0"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <InputGroupAddon addonType="append">
                          <Button
                            close
                            className="mr-2"
                            onClick={() => setSearchTerm("")}
                            style={{ outline: "none" }}
                          />
                        </InputGroupAddon>
                      )}
                    </InputGroup>
                  </Col>

                  <Col lg={countryFilter !== "all" && filterStateOptions.length > 0 ? "2" : "3"} md="6">
                    <CustomDropdown
                      value={countryFilter}
                      onChange={(val) => {
                        setCountryFilter(val);
                        setStateFilter("all");
                      }}
                      searchable={true}
                      placeholder="Filter by Country"
                      options={[
                        { label: "All Countries", value: "all" },
                        ...allCountries,
                      ]}
                    />
                  </Col>

                  {countryFilter !== "all" && filterStateOptions.length > 0 && (
                    <Col lg="2" md="6">
                      <CustomDropdown
                        value={stateFilter}
                        onChange={setStateFilter}
                        searchable={true}
                        placeholder="Filter by State"
                        options={[
                          { label: "All States", value: "all" },
                          ...filterStateOptions,
                        ]}
                      />
                    </Col>
                  )}

                  <Col lg="2" md="4">
                    <CustomDropdown
                      value={popularFilter}
                      onChange={setPopularFilter}
                      options={[
                        { label: "All Types", value: "all" },
                        { label: "Popular Only", value: "true" },
                        { label: "Standard Only", value: "false" },
                      ]}
                    />
                  </Col>

                  <Col lg="2" md="4">
                    <CustomDropdown
                      value={statusFilter}
                      onChange={setStatusFilter}
                      options={[
                        { label: "All Status", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" },
                      ]}
                    />
                  </Col>

                  <Col lg="1" md="4" className="text-right">
                    {(searchTerm ||
                      countryFilter !== "all" ||
                      stateFilter !== "all" ||
                      statusFilter !== "all" ||
                      popularFilter !== "all") && (
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger p-0 font-weight-bold"
                        onClick={resetAllFilters}
                        title="Reset Filters"
                      >
                        Reset
                      </Button>
                    )}
                  </Col>
                </Row>
              </CardHeader>

              {/* ================= TABLE ================= */}
              <div className="table-responsive" style={{ minHeight: "360px" }}>
                <Table className="align-items-center table-flush" hover>
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: "60px" }}>Order</th>
                      <th>Destination</th>
                      <th>Location Hierarchy</th>
                      <th style={{ minWidth: "220px" }}>Description</th>
                      <th className="text-center">Popular on App</th>
                      <th className="text-center">Status</th>
                      <th className="text-right" style={{ width: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && destinations.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader" />
                          <p className="mt-2 text-muted mb-0">Loading popular destinations...</p>
                        </td>
                      </tr>
                    ) : destinations.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="py-4 text-muted">
                            <FaGlobeAmericas size={40} className="text-light mb-3" />
                            <h4 className="text-muted">No Destinations Found</h4>
                            <p className="small mb-3">
                              {searchTerm || countryFilter !== "all" || statusFilter !== "all"
                                ? "No destinations match your active filters."
                                : "Start by adding your first popular travel destination."}
                            </p>
                            <Button
                              size="sm"
                              color="primary"
                              onClick={openCreateModal}
                              className="font-weight-bold"
                            >
                              <FaPlus className="mr-1" /> Add New Destination
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      destinations.map((dest, index) => {
                        const serialNum = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                        const fullImageUrl = dest.imageUrl || dest.image;

                        return (
                          <tr key={dest.uuid || dest._id} className="align-middle">
                            {/* Order */}
                            <td>
                              <Badge
                                color="light"
                                className="border text-dark font-weight-bold px-2 py-1"
                                title={`Display Order: ${dest.displayOrder || 0}`}
                              >
                                #{dest.displayOrder !== undefined ? dest.displayOrder : serialNum}
                              </Badge>
                            </td>

                            {/* Destination Name & Thumbnail */}
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="position-relative mr-3 rounded overflow-hidden shadow-sm"
                                  style={{
                                    width: "56px",
                                    height: "56px",
                                    flexShrink: 0,
                                    backgroundColor: "#f4f5f7",
                                    cursor: fullImageUrl ? "pointer" : "default",
                                    border: "1px solid #e9ecef",
                                  }}
                                  onClick={() => fullImageUrl && openImagePreview(fullImageUrl, dest.name)}
                                  title={fullImageUrl ? "Click to view full image" : "No image"}
                                >
                                  {fullImageUrl ? (
                                    <img
                                      src={fullImageUrl}
                                      alt={dest.name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        transition: "transform 0.2s ease",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="w-100 h-100 d-flex align-items-center justify-content-center text-muted"
                                      style={{ background: "#e9ecef" }}
                                    >
                                      <FaImage size={18} />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <span
                                    className="font-weight-bold text-dark d-block"
                                    style={{ fontSize: "14px", cursor: "pointer" }}
                                    onClick={() => openDetailModal(dest)}
                                  >
                                    {dest.name}
                                  </span>
                                  <small className="text-muted d-flex align-items-center mt-1">
                                    <FaMapMarkerAlt className="text-danger mr-1" size={11} />
                                    {[dest.city, dest.country].filter(Boolean).join(", ")}
                                  </small>
                                </div>
                              </div>
                            </td>

                            {/* Location Hierarchy */}
                            <td>
                              <div>
                                <span className="font-weight-600 text-dark d-block">
                                  {dest.country}
                                </span>
                                <small className="text-muted">
                                  {[dest.state, dest.city].filter(Boolean).join(" • ") || "All Regions"}
                                </small>
                              </div>
                            </td>

                            {/* Description preview */}
                            <td>
                              <p
                                className="text-muted mb-0 small"
                                style={{
                                  maxWidth: "260px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={dest.description}
                              >
                                {dest.description || <em className="text-light">No description provided</em>}
                              </p>
                            </td>

                            {/* Is Popular toggle */}
                            <td className="text-center">
                              <button
                                type="button"
                                onClick={() => handleTogglePopular(dest)}
                                className="btn btn-link p-0 text-decoration-none"
                                title="Click to toggle Popular status on Area Trek App"
                              >
                                {dest.isPopular ? (
                                  <Badge
                                    color="warning"
                                    pill
                                    className="px-3 py-2 font-weight-bold shadow-xs text-uppercase"
                                    style={{
                                      background: "linear-gradient(87deg, #fb6340 0, #fbb140 100%)",
                                      color: "#fff",
                                      letterSpacing: "0.5px",
                                      fontSize: "10px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <FaStar className="mr-1" size={10} /> Popular
                                  </Badge>
                                ) : (
                                  <Badge
                                    color="secondary"
                                    pill
                                    className="px-3 py-2 text-muted font-weight-bold"
                                    style={{
                                      background: "#e9ecef",
                                      letterSpacing: "0.5px",
                                      fontSize: "10px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <FaRegStar className="mr-1" size={10} /> Standard
                                  </Badge>
                                )}
                              </button>
                            </td>

                            {/* Status toggle */}
                            <td className="text-center">
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(dest)}
                                className="btn btn-link p-0 text-decoration-none"
                                title="Click to toggle Active/Inactive"
                              >
                                {dest.status === "active" ? (
                                  <Badge
                                    color="success"
                                    pill
                                    className="px-3 py-2 font-weight-bold"
                                    style={{
                                      backgroundColor: "#d4edda",
                                      color: "#155724",
                                      fontSize: "11px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <span
                                      className="d-inline-block rounded-circle bg-success mr-1"
                                      style={{ width: "6px", height: "6px" }}
                                    />
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge
                                    color="danger"
                                    pill
                                    className="px-3 py-2 font-weight-bold"
                                    style={{
                                      backgroundColor: "#f8d7da",
                                      color: "#721c24",
                                      fontSize: "11px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <span
                                      className="d-inline-block rounded-circle bg-danger mr-1"
                                      style={{ width: "6px", height: "6px" }}
                                    />
                                    Inactive
                                  </Badge>
                                )}
                              </button>
                            </td>

                            {/* Actions Dropdown */}
                            <td className="text-right">
                              <div className="d-flex justify-content-end align-items-center">
                                <UncontrolledDropdown className="table-action-dropdown">
                                  <DropdownToggle
                                    className="action-three-dots-btn"
                                    size="sm"
                                    color=""
                                    tag="button"
                                    type="button"
                                    title="Actions"
                                  >
                                    <i className="fas fa-ellipsis-v text-muted" />
                                  </DropdownToggle>
                                  <DropdownMenu right className="shadow border-0 py-2" style={{ zIndex: 1060 }}>
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openDetailModal(dest);
                                      }}
                                      className="py-2 text-dark"
                                    >
                                      <FaEye className="mr-2 text-primary" /> View Details
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openEditModal(dest);
                                      }}
                                      className="py-2 text-dark"
                                    >
                                      <FaEdit className="mr-2 text-info" /> Edit Destination
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleTogglePopular(dest);
                                      }}
                                      className="py-2 text-dark"
                                    >
                                      <FaStar className="mr-2 text-warning" />
                                      {dest.isPopular ? "Remove from Popular" : "Mark as Popular"}
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggleStatus(dest);
                                      }}
                                      className="py-2 text-dark"
                                    >
                                      {dest.status === "active" ? (
                                        <>
                                          <FaTimesCircle className="mr-2 text-danger" /> Deactivate
                                        </>
                                      ) : (
                                        <>
                                          <FaCheckCircle className="mr-2 text-success" /> Activate
                                        </>
                                      )}
                                    </DropdownItem>
                                    <DropdownItem divider />
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteDestination(dest);
                                      }}
                                      className="py-2 text-danger font-weight-bold"
                                    >
                                      <FaTrash className="mr-2 text-danger" /> Delete
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* ================= PAGINATION ================= */}
              {!loading && totalItems > 0 && (
                <CardFooter className="py-4 border-0">
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={(page) => fetchDestinationsList(page)}
                    itemName="popular destinations"
                  />
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ================= CREATE / EDIT MODAL ================= */}
      <Modal
        isOpen={formModalOpen}
        toggle={closeFormModal}
        size="lg"
        centered
        className="category-modal"
      >
        <ModalHeader toggle={closeFormModal}>
          <div className="category-modal-header-content">
            <div className="category-modal-header-icon">
              <i className={`fas ${editMode ? "fa-edit" : "fa-plus"}`} />
            </div>
            <div>
              <h4 className="mb-0">
                {editMode ? "Edit Destination" : "Add New Destination"}
              </h4>
              <small>
                {editMode
                  ? "Update destination details, sequence order, and visibility"
                  : "Create a new popular destination for Area Trek travelers"}
              </small>
            </div>
          </div>
        </ModalHeader>

        <Form onSubmit={handleFormSubmit} noValidate>
          <ModalBody style={{ overflow: "visible", padding: "24px" }}>
            <Row>
              {/* Destination Name */}
              <Col md="7">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-name">
                    Destination Name <span className="required-star">*</span>
                  </Label>
                  <Input
                    id="dest-name"
                    type="text"
                    className={`category-form-input ${touched.name && validationErrors.name ? "is-invalid" : ""}`}
                    placeholder="e.g. Paris & Eiffel Tower, Manali Snow Valley"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    onBlur={() => handleInputBlur("name")}
                    required
                  />
                  {touched.name && validationErrors.name && (
                    <div className="text-danger small mt-1">
                      <i className="fas fa-exclamation-circle mr-1" />
                      {validationErrors.name}
                    </div>
                  )}
                  <small className="category-input-help">
                    Enter the name of tourist spot, attraction or destination.
                  </small>
                </FormGroup>
              </Col>

              {/* Display Order */}
              <Col md="5">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-order">
                    Display Sequence Order <span className="text-muted small">(App Position)</span>
                  </Label>
                  <Input
                    id="dest-order"
                    type="number"
                    min="0"
                    className="category-form-input"
                    placeholder="1, 2, 3..."
                    value={formData.displayOrder}
                    onChange={(e) => handleInputChange("displayOrder", e.target.value)}
                    onBlur={() => handleInputBlur("displayOrder")}
                  />
                  <small className="category-input-help">
                    Sequence on app carousel (1 = first, 2 = second, etc.)
                  </small>
                  {touched.displayOrder && validationErrors.displayOrder && (
                    <div className="text-danger small mt-1">
                      <i className="fas fa-exclamation-circle mr-1" />
                      {validationErrors.displayOrder}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* Location Hierarchy: Country -> State -> City */}
            <Row>
              {/* Country Dropdown */}
              <Col md="4">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-country">
                    Country <span className="required-star">*</span>
                  </Label>
                  <Select
                    id="dest-country"
                    options={allCountries}
                    value={
                      formData.country
                        ? allCountries.find(
                            (opt) => opt.value.toLowerCase() === formData.country.toLowerCase()
                          ) || { label: formData.country, value: formData.country }
                        : null
                    }
                    onChange={(opt) => {
                      const val = opt ? opt.value : "";
                      setFormData((prev) => ({
                        ...prev,
                        country: val,
                        state: "",
                        city: "",
                      }));
                      const error = validateField("country", val);
                      setValidationErrors((prev) => ({ ...prev, country: error }));
                    }}
                    onBlur={() => handleInputBlur("country")}
                    placeholder="Search or select country"
                    isClearable
                    isSearchable
                    styles={modalSelectStyles}
                    menuPlacement="auto"
                  />
                  {touched.country && validationErrors.country && (
                    <div className="text-danger small mt-1">
                      <i className="fas fa-exclamation-circle mr-1" />
                      {validationErrors.country}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* State / Province Dropdown */}
              <Col md="4">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-state">
                    State / Province{" "}
                    <span className="text-muted small">
                      {formData.country ? `(${formStateOptions.length} available)` : "(Optional)"}
                    </span>
                  </Label>
                  <CreatableSelect
                    id="dest-state"
                    options={formStateOptions}
                    value={
                      formData.state
                        ? { label: formData.state, value: formData.state }
                        : null
                    }
                    onChange={(opt) => {
                      const val = opt ? opt.value : "";
                      setFormData((prev) => ({
                        ...prev,
                        state: val,
                        city: "",
                      }));
                    }}
                    placeholder={
                      formData.country
                        ? "Search or select state (or type)"
                        : "Select country first"
                    }
                    isDisabled={!formData.country}
                    isClearable
                    isSearchable
                    styles={modalSelectStyles}
                    menuPlacement="auto"
                  />
                  {formData.country && formStateOptions.length === 0 && (
                    <small className="category-input-help">
                      You can type a custom state name if needed
                    </small>
                  )}
                </FormGroup>
              </Col>

              {/* City / Town Dropdown */}
              <Col md="4">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-city">
                    City / Town{" "}
                    <span className="text-muted small">
                      {formData.state ? `(${formCityOptions.length} available)` : "(Optional)"}
                    </span>
                  </Label>
                  <CreatableSelect
                    id="dest-city"
                    options={formCityOptions}
                    value={
                      formData.city
                        ? { label: formData.city, value: formData.city }
                        : null
                    }
                    onChange={(opt) => {
                      const val = opt ? opt.value : "";
                      setFormData((prev) => ({
                        ...prev,
                        city: val,
                      }));
                    }}
                    placeholder={
                      formData.state
                        ? "Search or select city (or type)"
                        : formData.country
                        ? "Select state first"
                        : "Select country first"
                    }
                    isDisabled={!formData.state}
                    isClearable
                    isSearchable
                    styles={modalSelectStyles}
                    menuPlacement="auto"
                  />
                  {formData.state && (
                    <small className="category-input-help">
                      Select from list or type custom city / place
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* Description */}
            <FormGroup className="category-form-group mb-3">
              <Label htmlFor="dest-desc">
                Description / Highlights <span className="text-muted small">(Shown to travelers)</span>
              </Label>
              <Input
                id="dest-desc"
                type="textarea"
                rows="3"
                className="category-form-input"
                style={{ height: "auto" }}
                placeholder="Write summary about this destination, scenic spots, culture, best seasons to visit..."
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
              />
            </FormGroup>

            {/* Geographical Coordinates (Latitude & Longitude) */}
            <Row>
              <Col md="6">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-lat">
                    Latitude <span className="text-muted small">(-90 to +90, e.g. 32.2432)</span>
                  </Label>
                  <Input
                    id="dest-lat"
                    type="number"
                    step="any"
                    className={`category-form-input ${touched.latitude && validationErrors.latitude ? "is-invalid" : ""}`}
                    placeholder="e.g. 32.2432"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange("latitude", e.target.value)}
                    onBlur={() => handleInputBlur("latitude")}
                  />
                  {touched.latitude && validationErrors.latitude && (
                    <div className="text-danger small mt-1">
                      <i className="fas fa-exclamation-circle mr-1" />
                      {validationErrors.latitude}
                    </div>
                  )}
                  <small className="category-input-help">
                    Exact geographical latitude for maps and navigation.
                  </small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="category-form-group mb-3">
                  <Label htmlFor="dest-lng">
                    Longitude <span className="text-muted small">(-180 to +180, e.g. 77.1892)</span>
                  </Label>
                  <Input
                    id="dest-lng"
                    type="number"
                    step="any"
                    className={`category-form-input ${touched.longitude && validationErrors.longitude ? "is-invalid" : ""}`}
                    placeholder="e.g. 77.1892"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange("longitude", e.target.value)}
                    onBlur={() => handleInputBlur("longitude")}
                  />
                  {touched.longitude && validationErrors.longitude && (
                    <div className="text-danger small mt-1">
                      <i className="fas fa-exclamation-circle mr-1" />
                      {validationErrors.longitude}
                    </div>
                  )}
                  <small className="category-input-help">
                    Exact geographical longitude for maps and navigation.
                  </small>
                </FormGroup>
              </Col>
            </Row>

            {/* Modern Cover Image Upload (No generic browser button) */}
            <FormGroup className="category-form-group mb-3">
              <Label>Destination Cover Image</Label>
              <Row className="align-items-center">
                <Col md="7">
                  <div
                    className="border rounded p-3 d-flex flex-column align-items-center justify-content-center text-center position-relative"
                    style={{
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      borderColor: "#cbd5e1",
                      backgroundColor: "#f8f9fe",
                      borderRadius: "10px",
                      cursor: "pointer",
                      minHeight: "115px",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => document.getElementById("dest-image-upload").click()}
                  >
                    <input
                      type="file"
                      id="dest-image-upload"
                      accept="image/png, image/jpeg, image/jpg, image/webp"
                      style={{ display: "none" }}
                      onChange={handleImageFileChange}
                    />
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mb-1 text-primary"
                      style={{
                        width: "38px",
                        height: "38px",
                        backgroundColor: "#e0e7ff",
                      }}
                    >
                      <i className="fas fa-cloud-upload-alt fa-lg" />
                    </div>
                    <span className="font-weight-600 text-dark small">
                      {imageFile ? imageFile.name : "Click to select cover image"}
                    </span>
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                      PNG, JPG, WebP up to 5MB (1200x800 recommended)
                    </small>
                  </div>
                </Col>
                <Col md="5">
                  {imagePreview ? (
                    <div
                      className="position-relative rounded overflow-hidden border shadow-sm"
                      style={{ height: "115px", backgroundColor: "#fff" }}
                    >
                      <img
                        src={imagePreview}
                        alt="Cover Preview"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <Button
                        close
                        className="position-absolute bg-white text-danger rounded-circle p-1 shadow-sm"
                        style={{ top: "6px", right: "6px", opacity: 0.95 }}
                        onClick={clearSelectedImage}
                        title="Remove image"
                      />
                    </div>
                  ) : (
                    <div
                      className="rounded border border-dashed text-center text-muted d-flex flex-column align-items-center justify-content-center p-3"
                      style={{ height: "115px", backgroundColor: "#fafbfc" }}
                    >
                      <i className="fas fa-image fa-2x text-light mb-1" />
                      <span className="small text-muted">No image selected</span>
                    </div>
                  )}
                </Col>
              </Row>
            </FormGroup>

            {/* Visibility Settings */}
            <Row className="pt-2 border-top">
              <Col md="6">
                <div className="p-3 bg-secondary rounded mb-2 mb-md-0">
                  <CustomInput
                    type="switch"
                    id="dest-popular-switch"
                    name="isPopular"
                    label={
                      <div>
                        <span className="font-weight-bold d-block text-dark">
                          Mark as Popular Destination
                        </span>
                        <small className="text-muted">
                          Featured in the Popular Destinations carousel on Area Trek app
                        </small>
                      </div>
                    }
                    checked={formData.isPopular}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isPopular: e.target.checked }))}
                  />
                </div>
              </Col>
              <Col md="6">
                <div className="p-3 bg-secondary rounded">
                  <CustomInput
                    type="switch"
                    id="dest-status-switch"
                    name="status"
                    label={
                      <div>
                        <span className="font-weight-bold d-block text-dark">
                          Active Status
                        </span>
                        <small className="text-muted">
                          {formData.status === "active"
                            ? "Destination is live and visible to all users"
                            : "Destination is inactive and hidden from users"}
                        </small>
                      </div>
                    }
                    checked={formData.status === "active"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.checked ? "active" : "inactive",
                      }))
                    }
                  />
                </div>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter className="category-modal-footer">
            <Button
              className="category-modal-cancel-btn"
              size="sm"
              type="button"
              onClick={closeFormModal}
              disabled={submitting}
            >
              <i className="fas fa-times mr-1" /> Cancel
            </Button>
            <Button
              className="category-modal-save-btn"
              size="sm"
              type="submit"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-1 gradient-color-loader" /> Saving...
                </>
              ) : (
                <>
                  <i className={`fas ${editMode ? "fa-check" : "fa-plus"} mr-1`} />
                  {editMode ? "Update Destination" : "Save Destination"}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* ================= VIEW DETAILS MODAL ================= */}
      <Modal
        isOpen={detailModalOpen}
        toggle={() => setDetailModalOpen(false)}
        size="lg"
        centered
        className="user-table-user-model"
      >
        {selectedDestination && (
          <>
            <ModalHeader toggle={() => setDetailModalOpen(false)}>
              <div className="user-modal-header-content">
                <div className="user-modal-header-icon">
                  <i className="fas fa-map-marked-alt" />
                </div>
                <div>
                  <h4 className="mb-0">
                    {selectedDestination.name || "Destination Details"}
                  </h4>
                  <small>Popular Destination Details</small>
                </div>
              </div>
            </ModalHeader>
            <ModalBody>
              {detailLoading ? (
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader" />
                  <p className="mt-2 text-muted mb-0">Loading destination details...</p>
                </div>
              ) : (
                <>
                  {/* Hero Image */}
                  <div
                    className="rounded overflow-hidden mb-3 position-relative shadow-sm"
                    style={{
                      height: "240px",
                      backgroundColor: "#f4f5f7",
                    }}
                  >
                    {selectedDestination.imageUrl || selectedDestination.image ? (
                      <img
                        src={selectedDestination.imageUrl || selectedDestination.image}
                        alt={selectedDestination.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                        <FaGlobeAmericas size={40} className="mb-2 text-light" />
                        <span>No image available</span>
                      </div>
                    )}
                    <div
                      className="position-absolute d-flex align-items-center"
                      style={{ bottom: "12px", left: "12px", gap: "8px" }}
                    >
                      {selectedDestination.isPopular && (
                        <Badge
                          color="warning"
                          pill
                          className="px-3 py-2 font-weight-bold shadow-sm"
                        >
                          <FaStar className="mr-1" /> Popular Destination
                        </Badge>
                      )}
                      <Badge
                        color={selectedDestination.status === "active" ? "success" : "danger"}
                        pill
                        className="px-3 py-2 font-weight-bold shadow-sm"
                      >
                        {selectedDestination.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>

                  {/* Header Title */}
                  <h2 className="font-weight-bold text-dark mb-1">
                    {selectedDestination.name}
                  </h2>
                  <div className="d-flex align-items-center text-muted mb-3 flex-wrap">
                    <span className="mr-3">
                      <FaMapMarkerAlt className="text-danger mr-1" />
                      {[selectedDestination.city, selectedDestination.state, selectedDestination.country]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                    {selectedDestination.latitude !== null && selectedDestination.latitude !== undefined && (
                      <span className="mr-3 badge badge-light border text-primary">
                        <i className="fas fa-crosshairs mr-1" />
                        {selectedDestination.latitude}, {selectedDestination.longitude}
                      </span>
                    )}
                    <span className="badge badge-light border text-dark">
                      Display Order: #{selectedDestination.displayOrder || 0}
                    </span>
                  </div>

                  {/* Description */}
                  <div className="user-modal-section mb-3">
                    <div className="user-modal-section-title">
                      <i className="fas fa-align-left" />
                      <span>About Destination</span>
                    </div>
                    <p className="text-muted mb-0 small" style={{ lineHeight: "1.6" }}>
                      {selectedDestination.description || "No description provided for this destination."}
                    </p>
                  </div>

                  {/* App Insights */}
                  <Row className="mb-3">
                    <Col md="6">
                      <div className="user-detail-item mb-2 mb-md-0">
                        <div className="user-detail-icon">
                          <i className="fas fa-route" />
                        </div>
                        <div className="user-detail-content">
                          <span>Matching Tour Packages</span>
                          <strong>
                            {selectedDestination.linkedToursCount !== undefined
                              ? selectedDestination.linkedToursCount
                              : "—"}
                          </strong>
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="user-detail-item">
                        <div className="user-detail-icon">
                          <i className="fas fa-user-tie" />
                        </div>
                        <div className="user-detail-content">
                          <span>Local Guides in Area</span>
                          <strong>
                            {selectedDestination.linkedGuidesCount !== undefined
                              ? selectedDestination.linkedGuidesCount
                              : "—"}
                          </strong>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Metadata info */}
                  <div className="small text-muted border-top pt-2 d-flex justify-content-between flex-wrap">
                    <span>
                      <strong className="text-dark">Date Added:</strong>{" "}
                      {new Date(selectedDestination.createdAt || Date.now()).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    {selectedDestination.updatedAt && (
                      <span>
                        <strong className="text-dark">Last Updated:</strong>{" "}
                        {new Date(selectedDestination.updatedAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                </>
              )}
            </ModalBody>
            <ModalFooter className="user-modal-footer">
              <Button
                className="category-modal-cancel-btn"
                size="sm"
                type="button"
                onClick={() => setDetailModalOpen(false)}
              >
                <i className="fas fa-times mr-1" /> Close
              </Button>
              <Button
                className="category-modal-save-btn"
                size="sm"
                type="button"
                onClick={() => {
                  setDetailModalOpen(false);
                  openEditModal(selectedDestination);
                }}
              >
                <i className="fas fa-edit mr-1" /> Edit Destination
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* ================= LIGHTBOX IMAGE PREVIEW MODAL ================= */}
      <Modal
        isOpen={imagePreviewModalOpen}
        toggle={() => setImagePreviewModalOpen(false)}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={() => setImagePreviewModalOpen(false)}
          className="border-0 pb-0"
        >
          {previewImageTitle}
        </ModalHeader>
        <ModalBody className="text-center p-2">
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt={previewImageTitle}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default PopularDestinations;
